<template>
  <Teleport to="body">
    <div class="mv-notif-stack" v-if="notifs.length">
      <transition-group name="notif-drop" tag="div" class="mv-notif-inner">
        <div
          v-for="n in notifs"
          :key="n.uid"
          class="mv-notif-card"
          :class="n.type"
          @click="$emit('open', n.convId); $emit('dismiss', n.uid)"
        >
          <!-- Ícone -->
          <div class="mv-notif-icon">
            <!-- Nova mensagem -->
            <svg v-if="n.type === 'message'" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            <!-- Aguardando -->
            <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>

          <!-- Conteúdo -->
          <div class="mv-notif-body">
            <div class="mv-notif-title">{{ n.contactName }}</div>
            <div class="mv-notif-desc">{{ n.desc }}</div>
            <div v-if="n.team" class="mv-notif-team">{{ n.team }}</div>
          </div>

          <!-- Botões -->
          <button class="mv-notif-ver" @click.stop="$emit('open', n.convId); $emit('dismiss', n.uid)">Ver</button>
          <button class="mv-notif-close" @click.stop="$emit('dismiss', n.uid)" title="Fechar">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>

          <!-- Barra de progresso de auto-dismiss -->
          <div class="mv-notif-bar" :style="{ animationDuration: ttlMs + 'ms' }"></div>
        </div>
      </transition-group>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import type { MobileNotif } from '@/types/mobile'

defineProps<{ notifs: MobileNotif[]; ttlMs: number }>()
defineEmits<{ open: [convId: number]; dismiss: [uid: string] }>()
</script>

<style scoped>
/* ══ Notificações in-app ══ */
.mv-notif-stack {
  position: fixed;
  top: 0;
  left: 0; right: 0;
  z-index: 10000;
  pointer-events: none;
}
.mv-notif-inner {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: max(12px, env(safe-area-inset-top)) 12px 0;
  gap: 8px;
}

.mv-notif-card {
  background: #021627;
  border-radius: 14px;
  border-left: 4px solid #BC0C24;
  padding: 11px 12px 14px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  box-shadow: 0 8px 32px rgba(2,22,39,.55);
  pointer-events: all;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
}
.mv-notif-card.waiting { border-left-color: #FF6600; }
.mv-notif-card:active { opacity: .88; }

.mv-notif-icon {
  width: 32px; height: 32px;
  border-radius: 9px;
  background: rgba(188,12,36,.18);
  display: flex; align-items: center; justify-content: center;
  color: #BC0C24;
  flex-shrink: 0;
}
.mv-notif-card.waiting .mv-notif-icon {
  background: rgba(255,102,0,.15);
  color: #FF6600;
}

.mv-notif-body {
  flex: 1;
  min-width: 0;
}
.mv-notif-title {
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.mv-notif-desc {
  font-size: 11.5px;
  color: rgba(255,255,255,.6);
  margin-top: 1px;
}
.mv-notif-team {
  font-size: 10.5px;
  color: rgba(255,255,255,.35);
  margin-top: 2px;
}

.mv-notif-ver {
  background: #BC0C24;
  color: #fff;
  border: none;
  border-radius: 7px;
  padding: 5px 11px;
  font-size: 11.5px;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  flex-shrink: 0;
  align-self: center;
  transition: background .13s;
}
.mv-notif-card.waiting .mv-notif-ver { background: #FF6600; }
.mv-notif-ver:active { filter: brightness(.85); }

.mv-notif-close {
  background: none;
  border: none;
  color: rgba(255,255,255,.35);
  cursor: pointer;
  padding: 2px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  align-self: flex-start;
}
.mv-notif-close:hover { color: rgba(255,255,255,.7); }

/* Barra de progresso de auto-dismiss */
.mv-notif-bar {
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: 3px;
  background: rgba(188,12,36,.6);
  transform-origin: left;
  animation: notif-shrink linear forwards;
}
.mv-notif-card.waiting .mv-notif-bar { background: rgba(255,102,0,.5); }

@keyframes notif-shrink {
  from { transform: scaleX(1); }
  to   { transform: scaleX(0); }
}

/* Transições */
.notif-drop-enter-active { transition: transform .28s cubic-bezier(.2,0,.2,1), opacity .28s; }
.notif-drop-leave-active  { transition: transform .22s cubic-bezier(.4,0,1,1), opacity .22s; }
.notif-drop-enter-from    { transform: translateY(-110%); opacity: 0; }
.notif-drop-leave-to      { transform: translateY(-20px); opacity: 0; }
</style>
